import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';

// Section header for grouped options in the select dropdown
export const GlassGroupSubheader = styled(ListSubheader)`
  background: transparent !important;
  background-color: transparent !important;
  color: ${({ theme }) => theme.palette.primary.main};
  line-height: 32px;
  font-weight: 500;
  font-size: 12px;
`;

// Main text field with rounded corners and glassmorphism backdrop effect
export const RoundedTextField = styled(TextField)`
  border-radius: 8px;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);

  & .MuiOutlinedInput-root {
    border-radius: 8px;
    background: transparent;
    color: ${({ theme }) => theme.palette.white['06']};
    height: 40px;

    // Removes the default border outline in dark mode, but adds primary border in light mode
    & fieldset {
      border: ${({ theme }) =>
        theme.palette.mode === 'light'
          ? `1px solid ${theme.palette.primary.main}`
          : 'none'};
    }

    // Adds a subtle primary-color border on focus
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: 1px solid ${({ theme }) => theme.palette.primary['04']};
      background: transparent;
    }

    // Chevron/arrow icon color
    svg {
      color: ${({ theme }) => theme.palette.white['06']};
    }

    label {
      background: ${({ theme }) => theme.palette.white['06']};
    }
  }

  && {
    // Floating label when shrunk (value selected or input focused)
    .MuiInputLabel-shrink {
      background-color: transparent !important;
      padding: 0 4px;
      border-radius: 4px;
    }

    // Label in its default (non-shrunk) state, acting as placeholder
    .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
      color: ${({ theme }) =>
        theme.palette.mode === 'light'
          ? theme.palette.grey['01']
          : theme.palette.white['06']};
      background-color: transparent;
      transition: all 0.2s ease;
      transform: translate(16px, 9px) scale(1);
    }

    // Base label styles shared across all states
    .MuiInputLabel-root {
      color: ${({ theme }) =>
        theme.palette.mode === 'light'
          ? theme.palette.grey['01']
          : theme.palette.white['06']};
      background-color: transparent;
      transition: all 0.2s ease;
    }
  }

  // Select trigger area (the visible value row)
  & .MuiSelect-select {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 40px 0 16px;
    height: 40px;
    font-size: 14px;
    font-weight: 400;
    background-color: transparent !important;
  }
`;

// Off-screen row used only to measure chip widths
export const ChipMeasureRow = styled('div')`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  width: max-content;
  height: 0;
  overflow: hidden;
  font-size: 14px;
`;

// Individual option item inside the dropdown with glassmorphism hover/selected states
export const GlassMenuItem = styled(MenuItem)`
  font-size: 14px !important;
  font-weight: 500 !important;
  color: ${({ theme }) => theme.palette.white['06']} !important;

  &.Mui-selected {
    background: rgba(0, 200, 255, 0.2) !important;
  }

  &:hover {
    background: rgba(0, 200, 255, 0.1) !important;
  }
`;

// Returns the glassmorphism background styles for the dropdown Paper as a CSS string
export const getGlassPaperSx = (theme: Theme) =>
  `
  overflow: hidden;
  backdrop-filter: ${theme.palette.mode === 'dark' ? 'blur(20px)' : 'blur(14px) saturate(120%)'};
  -webkit-backdrop-filter: ${
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)'
  };
  background: ${
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)'
  };
`;

// Returns the glassmorphism background styles for the dropdown Paper as an SxProps object
export const getGlassPaperSxObject = (theme: Theme) => ({
  overflow: 'hidden',
  backdropFilter:
    theme.palette.mode === 'dark' ? 'blur(20px)' : 'blur(14px) saturate(120%)',
  WebkitBackdropFilter:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)',
});

// Returns the SxProps object that fits the Autocomplete input into the fixed 40px field height
export const getAutocompleteInputSx = (multiple: boolean) => ({
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '12px',
    // Keep chips on a single line so they don't overflow the fixed height
    ...(multiple && { flexWrap: 'nowrap', overflow: 'hidden' }),
    '& .MuiAutocomplete-input': {
      padding: '0 4px',
      minWidth: multiple ? '20px' : undefined,
    },
    // "+N" counter stays intact; the chip next to it shrinks with an ellipsis
    '& > span.MuiAutocomplete-tag': {
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
  },
});

// Returns the SxProps object for the Autocomplete dropdown listbox
export const getAutocompleteListboxSx = (theme: Theme) => ({
  fontSize: '14px !important',
  fontWeight: '500 !important',
  color: `${theme.palette.white['06']} !important`,
  backdropFilter:
    theme.palette.mode === 'dark' ? 'blur(20px)' : 'blur(14px) saturate(120%)',
  WebkitBackdropFilter:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)',
  // Highlight colors for focused and selected options
  '& .MuiAutocomplete-option': {
    '&.Mui-focused': {
      backgroundColor: 'rgba(0,200,255,0.1)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(0,200,255,0.2)',
    },
  },
});
